import {Button, Paper} from '@material-ui/core';
import {isLoaded, Loaded} from 'Core/models/Async';
import {getPasswordRequirements, ValidPassword} from 'Core/models/Password';
import {isRight} from 'fp-ts/lib/Either';
import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {showRequirementErrors} from 'Shared/components/ErrorPopover';
import Loading from 'Shared/components/Loading';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import {SecurityQuestion} from 'Users/api/api';
import 'Users/components/UserRecovery/PasswordReset/PasswordReset.sass';
import {changePassword, updatePassword, changeUpdatePasswordError} from 'Users/ducks/userProfile';
import {UserSecurityQuestions} from './UserSecurityQuestionsSection';

const passwordInputProps = {
  type: 'password',
};

type PasswordResetSectionProps = {
  onReset?: () => void;
};

const PasswordResetSection = ({onReset}: PasswordResetSectionProps) => {
  const dispatch = useDispatch();
  const [showPasswordErrors, setShowPasswordErrors] = useState(false);
  const {pwValue: password, userClaims, passwordUpdateError} = useSelector((state: ReducerState) => state.user.profile);
  const {securityQuestionForm} = useSelector((state: ReducerState) => state.user.userAccount);

  useEffect(() => {
    return () => {
      dispatch(changeUpdatePasswordError());
    };
  }, [dispatch, changeUpdatePasswordError]);

  const mapQuestions = (questions: UserSecurityQuestions): SecurityQuestion[] => [
    {
      id: questions.securityQuestionOne.question,
      response: questions.securityQuestionOne.answer,
    },
    {
      id: questions.securityQuestionTwo.question,
      response: questions.securityQuestionTwo.answer,
    },
  ];

  return isLoaded(userClaims) && isLoaded(securityQuestionForm) && isLoaded(password) ? (
    <Paper className="user-profile__user-info-section">
      <div className="user-info-section__header">
        <h3>Update Password</h3>
      </div>
      {passwordUpdateError && <div className="user-profile__error-message">{passwordUpdateError}</div>}
      <ValidatableTextField
        id="new-password-input"
        className="password-reset__new-password-input"
        label="New Password"
        showErrors={showRequirementErrors(showPasswordErrors)}
        errors={getPasswordRequirements(password.Data)}
        onChange={passwordLine1 =>
          dispatch(changePassword(new Loaded(ValidPassword.create(passwordLine1, password.Data.passwordLine2))))
        }
        value={password.Data.passwordLine1}
        inputProps={passwordInputProps}
      />
      <ValidatableTextField
        id="confirm-password-input"
        className="password-reset__confirm-password-input"
        label="Confirm Password"
        onBlur={() => setShowPasswordErrors(true)}
        showErrors={showRequirementErrors(showPasswordErrors)}
        errors={getPasswordRequirements(password.Data)}
        onChange={passwordLine2 =>
          dispatch(changePassword(new Loaded(ValidPassword.create(password.Data.passwordLine1, passwordLine2))))
        }
        value={password.Data.passwordLine2}
        inputProps={passwordInputProps}
      />
      <div className="password-reset__actions">
        <Button
          id="password-reset-submit-button"
          onClick={() => {
            if (password.Data instanceof ValidPassword) {
              dispatch(
                updatePassword(
                  userClaims.Data.id,
                  {
                    password: password.Data.passwordLine1,
                    securityQuestions: isRight(securityQuestionForm.Data)
                      ? mapQuestions(securityQuestionForm.Data.right)
                      : mapQuestions(securityQuestionForm.Data.left),
                  },
                  onReset
                )
              );
            }
          }}
          type="submit"
          variant="contained"
          color="primary"
          disabled={!(password.Data instanceof ValidPassword)}
        >
          Update Password
        </Button>
      </div>
    </Paper>
  ) : (
    <Paper className="user-profile__user-info-section">
      <Loading />
    </Paper>
  );
};

export default PasswordResetSection;
